import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { components } from '@bookingdei/api-client/types';
import { api } from '../api';

type FavoriteList = components['schemas']['FavoriteList'];

interface FavoritesContextValue {
  isSaved: (businessId: string) => boolean;
  toggle: (businessId: string) => void;
}

const FavoritesContext = createContext<FavoritesContextValue | null>(null);

// Save state for listing cards (FR-C6). The set of saved business ids is the server's
// (GET /v1/me/favorites); a click optimistically flips the card and calls
// PUT/DELETE /v1/me/favorites/{id}, reverting if the request fails. No card holds its
// own static "saved" boolean — the source of truth is here, backed by the API.
export function FavoritesProvider({ children }: { children: ReactNode }) {
  const [saved, setSaved] = useState<Set<string>>(new Set());

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = (await api.GET('/me/favorites')) as FavoriteList;
        if (alive) setSaved(new Set((list.items ?? []).map((f) => f.business_id)));
      } catch {
        // Signed-out (or offline): no saved set. A later save prompts sign-in via the
        // API's 401; the optimistic flip reverts.
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const toggle = useCallback((businessId: string) => {
    setSaved((prev) => {
      const wasSaved = prev.has(businessId);
      const next = new Set(prev);
      if (wasSaved) next.delete(businessId);
      else next.add(businessId);

      const request = wasSaved
        ? api.DELETE('/me/favorites/{business_id}', { params: { path: { business_id: businessId } } })
        : api.PUT('/me/favorites/{business_id}', { params: { path: { business_id: businessId } } });
      request.catch(() => {
        // Roll back on failure so the card never lies about server state.
        setSaved((current) => {
          const reverted = new Set(current);
          if (wasSaved) reverted.add(businessId);
          else reverted.delete(businessId);
          return reverted;
        });
      });

      return next;
    });
  }, []);

  const isSaved = useCallback((businessId: string) => saved.has(businessId), [saved]);

  return <FavoritesContext.Provider value={{ isSaved, toggle }}>{children}</FavoritesContext.Provider>;
}

export function useFavorites(): FavoritesContextValue {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error('useFavorites must be used within a FavoritesProvider');
  return ctx;
}
